import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import NumberLine from './NumberLine';
import NotationGuide from './NotationGuide';
import {
  defaultNumberLineConfig,
  formatElementSummary,
  parseNumberLineAnswerForDisplay,
} from './numberLineUtils';

/**
 * Read-only view for submitted number line answers
 * Shows student answer and optionally the correct answer for comparison
 */
const NumberLineReadOnly = ({
  config = defaultNumberLineConfig,
  studentAnswer = { elements: [] },
  correctAnswer = null,
  isCorrect = null,
  showCorrect = false,
}) => {
  const mergedConfig = { ...defaultNumberLineConfig, ...config };

  // Parse student elements
  const studentElements = useMemo(() => {
    if (studentAnswer?.elements) {
      return studentAnswer.elements;
    }
    return [];
  }, [studentAnswer]);

  // Parse correct elements
  const correctElements = useMemo(() => {
    if (!showCorrect || !correctAnswer) return [];
    if (correctAnswer?.elements) {
      return correctAnswer.elements;
    }
    return [];
  }, [showCorrect, correctAnswer]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Notation Guide */}
      <NotationGuide />

      {/* Number Line Visualization */}
      <NumberLine
        config={mergedConfig}
        elements={showCorrect ? [] : studentElements}
        readOnly={true}
        width={600}
        height={100}
        showCorrect={showCorrect}
        correctElements={correctElements}
        studentElements={studentElements}
        isCorrect={isCorrect}
      />

      {/* Student Answer Summary */}
      {studentElements.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Student Answer:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {studentElements.map((el, i) => (
              <Chip
                key={el.id || `student-${i}`}
                label={formatElementSummary(el)}
                size="small"
                color={
                  isCorrect === true
                    ? 'success'
                    : isCorrect === false
                    ? 'error'
                    : 'default'
                }
              />
            ))}
          </Box>
        </Box>
      )}

      {studentElements.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No answer submitted
        </Typography>
      )}

      {/* Correct Answer Summary (shown only to teachers/admins) */}
      {showCorrect && correctElements.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'success.main' }}>
            Correct Answer:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {correctElements.map((el, i) => (
              <Chip
                key={el.id || `correct-${i}`}
                label={formatElementSummary(el)}
                size="small"
                color="success"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NumberLineReadOnly;
